import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, FileText, Code, Zap, Globe, Hexagon } from 'lucide-react';
import './Hero.css';

const Hero = () => {
  const canvasRef = useRef(null);
  const [dataStream, setDataStream] = useState([]);

  useEffect(() => {
    // Animated data stream
    const interval = setInterval(() => {
      setDataStream(prev => {
        const newStream = [...prev, Math.random()].slice(-20);
        return newStream;
      });
    }, 100);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Advanced particle system with connections
    const particles = [];
    const particleCount = 120;

    class Particle {
      constructor() {
        this.reset();
      }

      reset() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.vx = (Math.random() - 0.5) * 0.5;
        this.vy = (Math.random() - 0.5) * 0.5;
        this.size = Math.random() * 3 + 1;
        this.life = Math.random() * 100;
        this.color = Math.random() > 0.5 ? '#00d4ff' : '#0091ff';
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;
        this.life -= 0.3;

        if (this.life <= 0 || this.x < 0 || this.x > canvas.width || this.y < 0 || this.y > canvas.height) {
          this.reset();
        }
      }

      draw() {
        const opacity = Math.min(this.life / 100, 0.8);
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.shadowBlur = 15;
        ctx.shadowColor = this.color;
        ctx.globalAlpha = opacity;
        ctx.fill();
        ctx.shadowBlur = 0;
        ctx.globalAlpha = 1;
      }
    }

    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach((particle, i) => {
        particles.slice(i + 1).forEach((otherParticle) => {
          const dx = particle.x - otherParticle.x;
          const dy = particle.y - otherParticle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 120) {
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(otherParticle.x, otherParticle.y);
            const gradient = ctx.createLinearGradient(particle.x, particle.y, otherParticle.x, otherParticle.y);
            gradient.addColorStop(0, particle.color);
            gradient.addColorStop(1, otherParticle.color);
            ctx.strokeStyle = gradient;
            ctx.globalAlpha = 0.2 * (1 - distance / 120);
            ctx.lineWidth = 1;
            ctx.stroke();
            ctx.globalAlpha = 1;
          }
        });

        particle.update();
        particle.draw();
      });

      requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // TRUE L1 blockchains only
  const l1Chains = [
    { name: 'Ethereum', color: '#627EEA' },
    { name: 'BNB Chain', color: '#F0B90B' },
    { name: 'Solana', color: '#14F195' },
    { name: 'Avalanche', color: '#E84142' },
    { name: 'Cosmos', color: '#2E3148' },
    { name: 'Polkadot', color: '#E6007A' },
    { name: 'Cardano', color: '#0033AD' },
    { name: 'Algorand', color: '#000000' },
  ];

  return (
    <section id="hero" className="hero-alien" data-testid="hero-section">
      <canvas ref={canvasRef} className="particle-canvas" />
      
      {/* Hexagonal grid pattern */}
      <div className="hex-grid-overlay"></div>
      
      {/* Animated scan lines */}
      <div className="scan-lines"></div>

      {/* Data streams */}
      <div className="data-streams">
        <div className="stream stream-left"></div>
        <div className="stream stream-right"></div>
      </div>

      <div className="hero-container-alien">
        {/* Holographic Logo Display */}
        <motion.div
          className="logo-hologram"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.5, ease: 'easeOut' }}
        >
          {/* Hexagonal frame */}
          <div className="hex-frame">
            <Hexagon className="hex-1" size={200} />
            <Hexagon className="hex-2" size={220} />
            <Hexagon className="hex-3" size={240} />
          </div>
          
          {/* Animated SVG Logo */}
          <motion.div
            className="logo-container-alien"
            animate={{
              rotateY: [0, 360],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: 'linear',
            }}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_592594db-80a8-424b-aef8-5e7847c76b85/artifacts/sl8inwlx_NOMOEX%20Logo%20%281%29.png"
              alt="Nomoex N"
              className="logo-alien-main"
            />
            {/* Holographic overlay */}
            <div className="hologram-overlay"></div>
          </motion.div>
          
          {/* Orbiting data points */}
          <div className="orbit-ring">
            <div className="orbit-dot" style={{ '--angle': '0deg' }}></div>
            <div className="orbit-dot" style={{ '--angle': '120deg' }}></div>
            <div className="orbit-dot" style={{ '--angle': '240deg' }}></div>
          </div>
        </motion.div>

        {/* Content */}
        <motion.div
          className="hero-content-alien"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          {/* Glitch Badge */}
          <motion.div
            className="badge-alien"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            <div className="badge-glitch">
              <Globe size={16} />
              <span className="badge-text-main">MULTI-L1 LAYER-2 BLOCKCHAIN</span>
              <span className="badge-text-glitch">MULTI-L1 LAYER-2 BLOCKCHAIN</span>
            </div>
          </motion.div>

          {/* Title with holographic effect */}
          <h1 className="title-alien">
            <span className="title-line-1">THE MULTI-L1</span>
            <span className="title-line-2 title-holographic">LAYER 2 CHAIN</span>
            <span className="title-line-3">FOR UNIFIED LIQUIDITY</span>
          </h1>

          <p className="subtitle-alien">
            <span className="text-glowing">Nomoex Chain</span> is a sovereign Layer-2 blockchain 
            that <span className="text-highlight">unifies liquidity, composability, and financial-grade reliability</span> across 
            multiple Layer-1 ecosystems with <span className="text-glowing">atomic cross-chain execution</span>.
          </p>

          {/* L1 Chain Network Map */}
          <div className="chains-network">
            <div className="network-label">
              <Hexagon size={12} className="label-icon" />
              <span>CONNECTED L1 NETWORKS</span>
            </div>
            <div className="chains-hex-grid-even">
              {l1Chains.map((chain, index) => (
                <motion.div
                  key={chain.name}
                  className="chain-hex"
                  style={{ '--chain-color': chain.color }}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1 + index * 0.1, duration: 0.4 }}
                  whileHover={{ scale: 1.1, y: -5 }}
                >
                  <div className="hex-border"></div>
                  <div className="chain-name">{chain.name}</div>
                  <div className="chain-pulse" style={{ borderColor: chain.color }}></div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Advanced CTA Buttons */}
          <motion.div
            className="cta-group-alien"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
          >
            <button
              onClick={() => scrollToSection('community')}
              className="cta-alien primary"
              data-testid="hero-join-waitlist-btn"
            >
              <span className="btn-bg-effect"></span>
              <span className="btn-content">
                <Zap size={20} />
                <span>Get Started</span>
                <ArrowRight size={20} />
              </span>
            </button>
            <button
              onClick={() => scrollToSection('developer')}
              className="cta-alien secondary"
              data-testid="hero-build-btn"
            >
              <Code size={20} />
              <span>Developer Portal</span>
            </button>
            <a
              href="https://customer-assets.emergentagent.com/job_592594db-80a8-424b-aef8-5e7847c76b85/artifacts/mcocvfv3_Nomoex%20Chain%20Whitepaper%20V2%20%281%29.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="cta-alien tertiary"
              data-testid="hero-whitepaper-btn"
            >
              <FileText size={20} />
              <span>Technical Docs</span>
            </a>
          </motion.div>

          {/* Advanced Stats Display */}
          <motion.div
            className="stats-alien"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.8 }}
          >
            <div className="stat-alien">
              <div className="stat-hex"></div>
              <div className="stat-content">
                <div className="stat-value">8+</div>
                <div className="stat-label">L1 NETWORKS</div>
              </div>
            </div>
            <div className="stat-connector"></div>
            <div className="stat-alien">
              <div className="stat-hex"></div>
              <div className="stat-content">
                <div className="stat-value">1-2s</div>
                <div className="stat-label">FINALITY TIME</div>
              </div>
            </div>
            <div className="stat-connector"></div>
            <div className="stat-alien">
              <div className="stat-hex"></div>
              <div className="stat-content">
                <div className="stat-value">HYBRID</div>
                <div className="stat-label">PoS + BFT</div>
              </div>
            </div>
            <div className="stat-connector"></div>
            <div className="stat-alien">
              <div className="stat-hex"></div>
              <div className="stat-content">
                <div className="stat-value">EVM/WASM</div>
                <div className="stat-label">DUAL RUNTIME</div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Corner accents */}
      <div className="corner-accent top-left"></div>
      <div className="corner-accent top-right"></div>
      <div className="corner-accent bottom-left"></div>
      <div className="corner-accent bottom-right"></div>
    </section>
  );
};

export default Hero;
